import { getLanguageDisplayName, normalizeLanguageKey } from "./programmingLanguages";

export type TrackStep = {
  id: string;
  type: "course" | "skill-test";
  title: string;
  shortDescription?: string;
  description?: string;
  difficulty?: string;
  status: "completed" | "in-progress" | "not-started";
  progressPercent?: number;
  resourceId: string;
};

export type LanguageTrack = {
  id: string;
  title: string;
  language: string;
  languageName: string;
  steps: TrackStep[];
  completedSteps: number;
  totalSteps: number;
  progressPercent: number;
};

const DIFFICULTY_ORDER: Record<string, number> = {
  beginner: 0,
  intermediate: 1,
  advanced: 2,
};

function sortByDifficulty<T extends { difficulty?: string; title?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const diff = (DIFFICULTY_ORDER[a.difficulty ?? ""] ?? 9) - (DIFFICULTY_ORDER[b.difficulty ?? ""] ?? 9);
    if (diff !== 0) return diff;
    return String(a.title ?? "").localeCompare(String(b.title ?? ""));
  });
}

function getCourseStatus(course: any): TrackStep["status"] {
  const pct = course.enrollment?.progressPercent ?? 0;
  if (pct >= 100 || course.enrollment?.isCompleted) return "completed";
  if (course.enrolled || pct > 0) return "in-progress";
  return "not-started";
}

function getSkillTestStatus(test: any): TrackStep["status"] {
  if (test.lastPassed) return "completed";
  if ((test.attempts ?? 0) > 0) return "in-progress";
  return "not-started";
}

export function buildLanguageTracks(courses: any[], skillTests: any[]): LanguageTrack[] {
  const byLang = new Map<string, { courses: any[]; tests: any[] }>();

  const addItem = (rawLang: string | undefined, item: any, kind: "course" | "skill-test") => {
    const key = normalizeLanguageKey(rawLang);
    if (!key) return;
    if (!byLang.has(key)) byLang.set(key, { courses: [], tests: [] });
    const bucket = byLang.get(key)!;
    if (kind === "course") bucket.courses.push(item);
    else bucket.tests.push(item);
  };

  courses.forEach((course) => addItem(course.programmingLanguage ?? course.language, course, "course"));
  skillTests.forEach((test) => addItem(test.programmingLanguage, test, "skill-test"));

  return [...byLang.entries()]
    .map(([language, { courses: langCourses, tests: langTests }]) => {
      const steps: TrackStep[] = [
        ...sortByDifficulty(langCourses).map((course) => {
          const resourceId = String(course.id ?? course._id);
          const progressPercent = course.enrollment?.progressPercent ?? 0;
          return {
            id: `course-${resourceId}`,
            type: "course" as const,
            title: course.title ?? "Course",
            shortDescription: course.shortDescription,
            description: course.description,
            difficulty: course.difficulty,
            status: getCourseStatus(course),
            progressPercent,
            resourceId,
          };
        }),
        ...sortByDifficulty(langTests).map((test) => {
          const resourceId = String(test.id ?? test._id);
          return {
            id: `test-${resourceId}`,
            type: "skill-test" as const,
            title: test.title ?? "Skill Test",
            shortDescription: test.shortDescription,
            description: test.description,
            difficulty: test.difficulty,
            status: getSkillTestStatus(test),
            progressPercent: test.lastPassed ? 100 : (test.attempts ?? 0) > 0 ? 50 : 0,
            resourceId,
          };
        }),
      ];

      const completedSteps = steps.filter((step) => step.status === "completed").length;
      const totalSteps = steps.length;
      const progressPercent = totalSteps ? Math.round((completedSteps / totalSteps) * 100) : 0;

      return {
        id: language,
        title: `${getLanguageDisplayName(language)} Roadmap`,
        language,
        languageName: getLanguageDisplayName(language),
        steps,
        completedSteps,
        totalSteps,
        progressPercent,
      };
    })
    .filter((track) => track.totalSteps > 0)
    .sort((a, b) => a.languageName.localeCompare(b.languageName));
}
